import { axiosInstance, printError, useUtilities } from "helpers";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getAllSites, unloadSites, updateSite } from "redux/reducers/sites";
import { useEffect } from "react";
import { t } from "i18next";
import { ApiResponseType, NotificationType } from "enums";
import { queryClient } from "client";

export async function getUserSites() {
  return axiosInstance
    .get(`usersitestoragedetails`, {
      params: {
        user_id: localStorage.getItem("user_id"),
        api_key: localStorage.getItem("apikey"),
      },
    })
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });
}

export function useGetUserSites() {
  return useQuery(["sitesData"], getUserSites);
}

export function useSites() {
  const { data, isLoading, error, isRefetching, isFetched } = useGetUserSites();
  const {
    dispatch,
    sites: { sites, loaded },
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded || isRefetching) return;
    if (isFetched) dispatch(getAllSites(data.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, error, loaded, isRefetching, isFetched]);

  return {
    data: sites,
    isLoading: isLoading || !loaded || isRefetching,
    error,
  };
}

export async function updateSiteDetails(siteData) {
  return axiosInstance
    .post("updatewebsitestatus", {
      api_key: localStorage.getItem("apikey"),
      ...siteData,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useUpdateSite(showNotification = true) {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateSiteDetails, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateSite(data.data[0]));
        if (showNotification) {
          notification({
            type: "SUCCESS",
            message: t(
              "siteSetting.generalSettings.updateSite.updateSuccessMessage"
            ),
          });
        }
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (err) =>
      notification({
        type: "error",
        message: err.message,
      }),
  });
}

const updateSiteURL = ({ website_url, website_id }) => {
  return axiosInstance
    .post("websites/url/update", {
      api_key: localStorage.getItem("apikey"),
      website_id,
      website_url,
    })
    .then((res) => res.data);
};

export const useUpdateSiteURL = () => {
  const { notification, dispatch } = useUtilities();
  return useMutation(updateSiteURL, {
    onSuccess: (res) => {
      if (res.status_code === ApiResponseType.SUCCESS) {
        notification({
          type: NotificationType.SUCCESS,
          message: t(
            "siteSetting.advanced.siteDetails.updateConnectedSite.api.success"
          ),
        });
        queryClient.invalidateQueries(["sitesData"]);
        dispatch(unloadSites());
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(),
        });
      }
    },
    onError: (error) => {
      notification({
        type: NotificationType.ERROR,
        message: printError(error.response.data.message),
      });
    },
  });
};
